import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Plus, FileText, Video, Link as LinkIcon, Search, Filter, 
  Trash2, Edit2, Download, Eye, AlertCircle, RefreshCw, Upload, 
  File, Globe, X, Check, Loader
} from 'lucide-react';
import knowledgeService from '../../api/services/knowledgeService';

export default function BaseConnaissanceConnected() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'text',
    source: '',
    metadata: {
      tags: [],
      category: '',
    }
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadDocuments();
  }, [filterType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (filterType !== 'all') {
        params.type = filterType;
      }
      
      const response = await knowledgeService.getDocuments(params);
      console.log('✅ Documents chargés:', response);
      
      if (response.success && response.data) {
        const docList = Array.isArray(response.data) ? response.data : [];
        setDocuments(docList);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('❌ Erreur chargement documents:', err);
      setError(err.message || 'Erreur lors du chargement des documents');
      setLoading(false);
    }
  };

  const handleAddDocument = () => {
    setFormData({
      title: '',
      content: '',
      type: 'text',
      source: '',
      metadata: {
        tags: [],
        category: '',
      }
    });
    setSelectedDoc(null);
    setSaveError('');
    setShowAddModal(true);
  };

  const handleEditDocument = (doc) => {
    setFormData({
      title: doc.title || '',
      content: doc.content || '',
      type: doc.type || 'text',
      source: doc.source || '',
      metadata: {
        tags: doc.metadata?.tags || [],
        category: doc.metadata?.category || '',
      }
    });
    setSelectedDoc(doc);
    setSaveError('');
    setShowAddModal(true);
  };

  const handleSaveDocument = async () => {
    if (!formData.title || !formData.content) {
      setSaveError('Le titre et le contenu sont requis');
      return;
    }

    try {
      setSaving(true);
      setSaveError('');
      
      const docData = {
        ...formData,
        metadata: {
          ...formData.metadata,
          tags: formData.metadata.tags.filter(t => t.trim()),
        }
      };
      
      if (selectedDoc) {
        // Mise à jour
        await knowledgeService.updateDocument(selectedDoc._id, docData);
        console.log('✅ Document mis à jour');
      } else {
        // Création
        await knowledgeService.uploadDocument(docData);
        console.log('✅ Document créé');
      }
      
      setShowAddModal(false);
      await loadDocuments();
      setSaving(false);
    } catch (err) {
      console.error('❌ Erreur sauvegarde:', err);
      setSaveError(err.message || 'Erreur lors de la sauvegarde');
      setSaving(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDoc) return;
    
    try {
      await knowledgeService.deleteDocument(selectedDoc._id);
      console.log('✅ Document supprimé');
      
      setShowDeleteConfirm(false);
      setSelectedDoc(null);
      await loadDocuments();
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const confirmDelete = (doc) => {
    setSelectedDoc(doc);
    setShowDeleteConfirm(true);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'pdf':
      case 'file':
        return FileText;
      case 'url':
        return LinkIcon;
      case 'video':
        return Video;
      default:
        return File;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      text: 'Texte',
      pdf: 'PDF',
      url: 'Lien',
      file: 'Fichier',
      video: 'Vidéo'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-500/20 text-green-500';
      case 'processing':
        return 'bg-yellow-500/20 text-yellow-500';
      case 'error':
        return 'bg-red-500/20 text-red-500';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      ready: 'Prêt',
      processing: 'En cours',
      error: 'Erreur'
    };
    return labels[status] || status;
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        doc.title?.toLowerCase().includes(query) ||
        doc.content?.toLowerCase().includes(query) ||
        doc.metadata?.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const stats = {
    total: documents.length,
    ready: documents.filter(d => d.status === 'ready').length,
    processing: documents.filter(d => d.status === 'processing').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <h3 className="text-red-500 font-semibold">Erreur</h3>
        </div>
        <p className="text-gray-400 mb-4">{error}</p>
        <button 
          onClick={loadDocuments}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Ressources</p>
          <h2 className="text-ink font-semibold" style={{ fontSize: '18px' }}>Base de connaissances</h2>
          <p className="text-label text-sm mt-1">
            {stats.total} document{stats.total > 1 ? 's' : ''} • {stats.ready} prêt{stats.ready > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleAddDocument}
          className="w-full sm:w-auto justify-center bg-accent-green text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-accent-green/90 transition-all flex-shrink-0"
        >
          <Plus size={20} />
          Ajouter un document
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-label" size={18} />
            <input
              type="text"
              placeholder="Rechercher dans les documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-ink pl-10 pr-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
          </div>

          <div className="flex gap-2">
            <div className="flex gap-2 overflow-x-auto flex-1 lg:flex-none">
              {[
                { id: 'all', label: 'Tous' },
                { id: 'text', label: 'Texte' },
                { id: 'pdf', label: 'PDF' },
                { id: 'url', label: 'Liens' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setFilterType(filter.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filterType === filter.id
                      ? 'bg-accent-green text-black'
                      : 'text-label hover:bg-[var(--c-hover)]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <button
              onClick={loadDocuments}
              className="p-2 hover:bg-[var(--c-hover)] rounded-lg transition-all flex-shrink-0"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            >
              <RefreshCw className="text-label" size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Liste des documents */}
      {filteredDocuments.length === 0 ? (
        <div className="card p-8 sm:p-12 text-center">
          <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-ink font-semibold mb-2">
            {searchQuery ? 'Aucun résultat' : 'Aucun document'}
          </h3>
          <p className="text-gray-400 mb-6">
            {searchQuery 
              ? 'Essayez avec d\'autres mots-clés' 
              : 'Commencez par ajouter votre premier document'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleAddDocument}
              className="px-6 py-3 bg-accent-green text-black rounded-lg font-bold inline-flex items-center gap-2 hover:bg-accent-green/90 transition-all"
            >
              <Plus size={20} />
              Ajouter un document
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((doc) => {
            const Icon = getIcon(doc.type);
            return (
              <motion.div
                key={doc._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-6 shadow-custom hover:shadow-green transition-all cursor-pointer border border-transparent hover:border-accent-green/30 group"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-accent-green/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="text-accent-green" size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-ink font-semibold mb-1 truncate">{doc.title}</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(doc.status)}`}>
                        {getStatusLabel(doc.status)}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent-green/20 text-accent-green">
                        {getTypeLabel(doc.type)}
                      </span>
                    </div>
                  </div>
                </div>

                {doc.metadata?.tags && doc.metadata.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {doc.metadata.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 text-label rounded" style={{ background: 'var(--c-primary-dark)' }}>
                        #{tag}
                      </span>
                    ))}
                    {doc.metadata.tags.length > 3 && (
                      <span className="text-xs px-2 py-1 text-label rounded" style={{ background: 'var(--c-primary-dark)' }}>
                        +{doc.metadata.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-label text-sm mb-4 line-clamp-2">
                  {doc.content?.substring(0, 100)}...
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-primary-sidebar">
                  <span className="text-xs text-label">
                    {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                  </span>
                  <div className="flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEditDocument(doc)}
                      className="p-2 hover:bg-[var(--c-hover)] rounded-lg transition-all"
                    >
                      <Edit2 className="text-label hover:text-accent-green" size={16} />
                    </button>
                    <button
                      onClick={() => confirmDelete(doc)}
                      className="p-2 hover:bg-[var(--c-hover)] rounded-lg transition-all"
                    >
                      <Trash2 className="text-label hover:text-red-500" size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal Ajouter/Modifier */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-ink font-semibold text-lg">
                  {selectedDoc ? 'Modifier le document' : 'Ajouter un document'}
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-[var(--c-hover)] rounded-lg transition-all"
                >
                  <X className="text-label" size={20} />
                </button>
              </div>

              {saveError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="text-red-500" size={16} />
                  <p className="text-red-400 text-sm">{saveError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-ink font-medium mb-2 block">Titre *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="Titre du document"
                  />
                </div>

                <div>
                  <label className="text-ink font-medium mb-2 block">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  >
                    <option value="text">Texte</option>
                    <option value="pdf">PDF</option>
                    <option value="url">Lien URL</option>
                    <option value="file">Fichier</option>
                  </select>
                </div>

                {(formData.type === 'url' || formData.type === 'file') && (
                  <div>
                    <label className="text-ink font-medium mb-2 block">
                      {formData.type === 'url' ? 'URL' : 'Chemin du fichier'}
                    </label>
                    <input
                      type="text"
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      placeholder={formData.type === 'url' ? 'https://...' : '/path/to/file'}
                    />
                  </div>
                )}

                <div>
                  <label className="text-ink font-medium mb-2 block">Contenu *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={8}
                    className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="Contenu du document..."
                  />
                </div>

                <div>
                  <label className="text-ink font-medium mb-2 block">Catégorie</label>
                  <input
                    type="text"
                    value={formData.metadata.category}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metadata: { ...formData.metadata, category: e.target.value }
                    })}
                    className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="Ex: Documentation, FAQ, Guide"
                  />
                </div>

                <div>
                  <label className="text-ink font-medium mb-2 block">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    type="text"
                    value={formData.metadata.tags.join(', ')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metadata: { 
                        ...formData.metadata, 
                        tags: e.target.value.split(',').map(t => t.trim())
                      }
                    })}
                    className="w-full text-ink px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="ia, chatbot, guide"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 text-ink rounded-lg font-medium transition-all hover:bg-[var(--c-hover)]" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveDocument}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-accent-green text-black rounded-lg font-bold hover:bg-accent-green/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {selectedDoc ? 'Mettre à jour' : 'Créer'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Confirmation suppression */}
      <AnimatePresence>
        {showDeleteConfirm && selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-500" size={24} />
                </div>
                <div>
                  <h3 className="text-ink font-semibold">Supprimer le document ?</h3>
                  <p className="text-label text-sm">Cette action est irréversible</p>
                </div>
              </div>
              
              <p className="text-label mb-6">
                Êtes-vous sûr de vouloir supprimer <strong className="text-ink">{selectedDoc.title}</strong> ?
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 text-ink rounded-lg font-medium transition-all hover:bg-[var(--c-hover)]" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteDocument}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all"
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
